// src/pages/Reports/IssuanceReporting/ReconciliationDiscrepancy/TicketCancellationTab.tsx
// RPT-014 (README.md mục 7) — vé hủy, CHỈ đọc (auto-log từ BE khi hủy kế hoạch phát hành sau khi
// đã sinh vé — không có thao tác tạo/sửa/xoá ở màn này).

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getPaginatedTicketCancellationsApi } from 'helpers/fakebackend_helper';
import { TICKET_CANCELLATION } from 'helpers/url_helper';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, ReportPaginationState, TicketCancellation } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const TicketCancellationTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<TicketCancellation[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);

  const buildFilterParams = useCallback(
    () => ({ IssuancePlanId: planId || undefined, FromDate: fromDate || undefined, ToDate: toDate || undefined }),
    [planId, fromDate, toDate],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPaginatedTicketCancellationsApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_danh_sach_ve_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, fromDate, toDate]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(TICKET_CANCELLATION, buildFilterParams(), format, 'VeHuy');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={4}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('tu_ngay_label')}</Label>
          <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('den_ngay_label')}</Label>
          <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); resetPage(); }} />
        </Col>
        <Col md={2} className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ky_phat_hanh_label')}</th>
                <th>{st('ky_hieu_label')}</th>
                <th>{st('dai_so_column')}</th>
                <th className="text-end">{st('sl_column')}</th>
                <th className="text-end">{st('gia_tri_column')}</th>
                <th>{st('ly_do_label')}</th>
                <th>{st('ngay_huy_column')}</th>
                <th>{st('trang_thai_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.planCode}</td>
                  <td>{row.symbol || '-'}</td>
                  <td>{row.rangeStart && row.rangeEnd ? `${row.rangeStart} - ${row.rangeEnd}` : '-'}</td>
                  <td className="text-end">{formatQty(row.quantity)}</td>
                  <td className="text-end">{formatCurrency(row.value)}</td>
                  <td>{row.reason}</td>
                  <td>{formatDateTime(row.approvedAt || row.requestedAt)}</td>
                  <td><span className="badge bg-danger-subtle text-danger">{row.statusName}</span></td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default TicketCancellationTab;
